import type { GridCamera } from "../world-zoom.ts";

/**
 * Where a world point lands for the UI camera (ADR-061, "world-attached UI has a layer and a camera of its
 * own"). The world camera draws a world point at `size x origin x (1 - zoom) - zoom x scroll + zoom x point`
 * device pixels on each axis, the translation Phaser 4.2.1's `Camera.preRender` builds. The UI camera has
 * its origin at the top left, no scroll and the UI scale as its zoom, so it draws a UI position at that
 * position times the scale. A projected point is rounded to a whole device pixel and then divided by the
 * UI scale: it may fall between logical pixels, but never between device pixels. Snapping to logical
 * pixels instead would make a nameplate judder wherever the two scales differ, since the world moves in
 * steps of the world zoom (4 against 3 at 2560 by 1440).
 */

/**
 * A point written in place, so a caller projecting every frame allocates nothing.
 */
export interface MutablePoint {
  x: number;
  y: number;
}

/**
 * The device pixel a world point is drawn at by a camera, on one axis.
 *
 * @param size The camera's width or height in device pixels.
 * @param origin The camera's origin on that axis, as a fraction of the size.
 * @param zoom The camera's zoom.
 * @param scroll The camera's scroll on that axis, in world pixels.
 * @param point The world coordinate.
 * @returns The nearest whole device pixel.
 */
function deviceAxis(size: number, origin: number, zoom: number, scroll: number, point: number): number {
  return Math.round(size * origin * (1 - zoom) - zoom * scroll + zoom * point);
}

/**
 * The whole device pixel a world point is drawn at.
 *
 * @param camera The world camera, with the scroll it renders this frame.
 * @param worldX The point's x in world pixels.
 * @param worldY The point's y in world pixels.
 * @param out Where to write the result.
 * @returns `out`, holding the device pixel.
 */
export function projectToDevice(camera: GridCamera, worldX: number, worldY: number, out: MutablePoint): MutablePoint {
  out.x = deviceAxis(camera.width, camera.originX, camera.zoom, camera.scrollX, worldX);
  out.y = deviceAxis(camera.height, camera.originY, camera.zoom, camera.scrollY, worldY);
  return out;
}

/**
 * The UI-layer position that draws over a world point at a whole device pixel.
 *
 * @param camera The world camera, with the scroll it renders this frame.
 * @param scale The UI scale, which is the UI camera's zoom.
 * @param worldX The point's x in world pixels.
 * @param worldY The point's y in world pixels.
 * @param out Where to write the result.
 * @returns `out`, holding the position in logical pixels, a whole number of device pixels from the origin.
 * @throws RangeError when the scale is not a whole number of at least 1.
 */
export function projectToUi(camera: GridCamera, scale: number, worldX: number, worldY: number, out: MutablePoint): MutablePoint {
  if (!Number.isInteger(scale) || scale < 1) {
    throw new RangeError(`UI scale ${scale} is not a whole number of at least 1`);
  }
  projectToDevice(camera, worldX, worldY, out);
  out.x /= scale;
  out.y /= scale;
  return out;
}
